import React, { useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import {
  Calendar as CalendarIcon, Clock, User, Plus,
  Edit, Trash2, Bell, Send, Loader2,
  MapPin, Wrench, DollarSign, UserCheck, ChevronRight, Car, PhoneIncoming,
  Pencil, Check, X, Briefcase
} from "lucide-react";
import { Virtuoso } from "react-virtuoso";

const VIRTUALIZATION_THRESHOLD = 100;
import { format } from "date-fns";
import {
  getStatusConfig, getServiceIconStyle, getInitials, getRelativeTime, stripVehicleFromTitle
} from "./jobCardUtils";
import { getDisplayAddress } from "../../utils/job-form-utils";
import { formatJobScheduleDisplay } from "../../utils/dateUtils";
import { JobSortToggle, readJobSortPref, writeJobSortPref, compareBySchedule } from "./JobSortToggle";

const getDisplayTime = (job) => {
  if ((job.status === "completed" || job.status === "completed_pending_payment") && job.completed_at) {
    try { return format(new Date(job.completed_at), "h:mm a"); } catch { /* fall through */ }
  }
  return formatJobScheduleDisplay(job) || null;
};

const JobCard = ({
  job, index, onPreview, handleEdit, confirmDelete,
  handleSendReminder, handleSendToTech, sendingToTech,
  onRenameJob, sourceMap, onSourceClick,
}) => {
  const status = getStatusConfig(job.status);
  const svcStyle = getServiceIconStyle(job.service_type);
  const price = job.total_charged || job.price || 0;
  const techName = job.assigned_to_name;
  const relTime = getRelativeTime(job.created_at);
  const displayTime = getDisplayTime(job);
  const sourceName = job.source_name || (job.source_id && sourceMap?.[job.source_id]) || null;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const startRename = (e) => {
    e.stopPropagation();
    setTitleDraft(job.title || "");
    setEditingTitle(true);
  };
  const saveRename = async (e) => {
    e?.stopPropagation();
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (t && t !== job.title) await onRenameJob?.(job, t);
  };

  return (
    <div
      className="group relative min-w-0 max-w-full"
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`list-job-card-${job.job_id}`}
    >
      <div
        onClick={() => onPreview?.(job)}
        className="relative overflow-hidden rounded-xl cursor-pointer bg-card/60 backdrop-blur-md border border-border/60 transition-all duration-300 ease-out hover:translate-y-[-2px] hover:shadow-lg hover:border-opacity-60 job-card-enter"
      >
        {/* Status accent line */}
        <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${status.gradient} opacity-80`} style={status.barStyle} />

        <div className="p-3.5 sm:p-5">
          {/* Status + Price row */}
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  {status.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.dot} opacity-75`} style={status.dotStyle} />}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${status.dot}`} style={status.dotStyle} />
                </span>
                <span className={`text-[11px] font-semibold tracking-wide uppercase ${status.text}`} style={status.textStyle}>{status.label}</span>
              </div>
              {relTime && <span className="text-[10px] text-muted-foreground/60 hidden sm:inline">{relTime}</span>}
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/20">
              <DollarSign className="w-3 h-3 text-[#FFCC00]" />
              <span className="text-sm font-bold text-[#FFCC00] tabular-nums">
                {price.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Title + Service Icon */}
          <div className="flex items-start gap-2.5 mb-2.5">
            <div className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center ${svcStyle.bg}`}>
              {(job.vehicle_year || job.vehicle_make || job.service_type === "locksmith")
                ? <Car className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${svcStyle.color}`} />
                : <Wrench className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${svcStyle.color}`} />
              }
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {editingTitle ? (
                  <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(e);
                        if (e.key === "Escape") { e.stopPropagation(); setEditingTitle(false); }
                      }}
                      className="flex-1 min-w-0 h-7 px-2 rounded-md bg-theme-surface border border-border/60 text-[13px] sm:text-[14px] text-foreground outline-none focus:border-[#FFCC00]/60"
                      placeholder="Job title"
                      data-testid={`job-title-input-${job.job_id}`}
                    />
                    <button type="button" onClick={saveRename} className="p-1 rounded-md text-emerald-500 hover:bg-emerald-500/10" title="Save" data-testid={`job-title-save-${job.job_id}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setEditingTitle(false); }} className="p-1 rounded-md text-muted-foreground hover:bg-theme-hover" title="Cancel" data-testid={`job-title-cancel-${job.job_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-foreground text-[13px] sm:text-[15px] leading-tight truncate font-['Manrope']">
                      {stripVehicleFromTitle(job)}
                    </h3>
                    <button
                      type="button"
                      onClick={startRename}
                      className="p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-theme-hover flex-shrink-0 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      title="Rename job"
                      data-testid={`rename-job-${job.job_id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {job.job_display_id && (
                      <span className="text-[10px] font-mono text-muted-foreground/50 bg-muted/40 px-1.5 py-0.5 rounded flex-shrink-0">{job.job_display_id}</span>
                    )}
                    {job.ticket_number && (
                      <span className="text-[10px] font-mono text-blue-500/70 bg-blue-500/10 px-1.5 py-0.5 rounded flex-shrink-0">{job.ticket_number}</span>
                    )}
                  </>
                )}
              </div>
              {/* Vehicle info as subtitle */}
              {(job.vehicle_year || job.vehicle_make || job.vehicle_model) && (
                <p className="text-[11px] text-blue-500 mt-0.5 flex items-center gap-1 truncate">
                  <Car className="w-3 h-3 shrink-0" />
                  {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
                </p>
              )}
              {job.service_type && job.service_type !== "other" && !(job.vehicle_year || job.vehicle_make || job.vehicle_model) && (
                <span className="text-[11px] text-muted-foreground/70 capitalize mt-0.5 block truncate">{job.service_name || job.service_type}</span>
              )}
            </div>
          </div>

          {/* Info chips */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
            {(job.lead_source === "phone_call" || job.created_via === "phone_call" || job.is_auto_lead) && (
              <div
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/25 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
                title="Auto-created from inbound call"
                data-testid={`job-from-call-badge-${job.job_id}`}
              >
                <PhoneIncoming className="w-3 h-3" />
                <span>From Call</span>
              </div>
            )}
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-surface text-[11px] text-muted-foreground max-w-[160px] sm:max-w-[180px]">
              <User className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="truncate">{job.customer_name || "Unknown"}</span>
            </div>
            {job.scheduled_date && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-surface text-[11px] text-muted-foreground">
                <CalendarIcon className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span>{format(new Date(job.scheduled_date), "MMM d")}</span>
              </div>
            )}
            {displayTime && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-surface text-[11px] text-muted-foreground">
                <Clock className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span>{displayTime}</span>
              </div>
            )}
            {sourceName && (
              <div
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/10 text-[11px] text-purple-500 dark:text-purple-400 max-w-[140px] sm:max-w-[180px] min-w-0 ${job.source_id && onSourceClick ? "cursor-pointer hover:bg-purple-500/25 hover:ring-1 hover:ring-purple-500/40 transition-colors" : ""}`}
                title={job.source_id && onSourceClick ? `Filter jobs by source: ${sourceName}` : `Job source: ${sourceName}`}
                onClick={job.source_id && onSourceClick ? (e) => { e.stopPropagation(); onSourceClick(job.source_id); } : undefined}
                data-testid={`job-source-chip-${job.job_id}`}
              >
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{sourceName}</span>
              </div>
            )}
            {job.customer_address && (
              <div className="hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-surface text-[11px] text-muted-foreground max-w-[200px]">
                <MapPin className="w-3 h-3 flex-shrink-0 text-muted-foreground/70" />
                <span className="truncate">{getDisplayAddress(job)}</span>
              </div>
            )}
            {job.store_location_name && (
              <div
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/10 text-[11px] text-blue-500 max-w-[140px] sm:max-w-[180px] min-w-0"
                title={job.store_location_name}
              >
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{job.store_location_name}</span>
              </div>
            )}
          </div>

          {/* Bottom: Technician + Actions */}
          <div className="flex items-center justify-between pt-2.5 border-t border-border/40">
            <div className="flex items-center gap-2 min-w-0">
              {techName ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#0046BE]/20 border border-[#0046BE]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] sm:text-[10px] font-bold text-[#0046BE]">{getInitials(techName)}</span>
                  </div>
                  <span className="text-[11px] sm:text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-[120px]">{techName}</span>
                  {job.assignment_status === "accepted" && <UserCheck className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
                </div>
              ) : (
                <span className="text-[11px] text-muted-foreground/50 italic">Unassigned</span>
              )}
            </div>
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              {job.assigned_to && job.assignment_status !== "accepted" && (
                <Button variant="ghost" size="icon" onClick={() => handleSendToTech(job)} disabled={sendingToTech === job.job_id}
                  className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg" title="Send to technician" data-testid={`send-to-tech-${job.job_id}`}>
                  {sendingToTech === job.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              )}
              {job.status === "scheduled" && (
                <Button variant="ghost" size="icon" onClick={() => handleSendReminder(job)}
                  className="h-7 w-7 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg" title="Send reminder" data-testid={`send-reminder-${job.job_id}`}>
                  <Bell className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => handleEdit(job)}
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-theme-hover rounded-lg" title="Edit job" data-testid={`edit-job-${job.job_id}`}>
                <Edit className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => confirmDelete(job)}
                className="h-7 w-7 text-red-400/70 hover:text-red-300 hover:bg-red-500/10 rounded-lg" title="Delete job" data-testid={`delete-job-${job.job_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobListView = ({
  jobs, openNewJobDialog, handleEdit, confirmDelete,
  handleSendReminder, handleSendToTech, sendingToTech, onPreview,
  onEndReached, loadingMore = false, hasMore = false,
  onRenameJob, sourceMap, onSourceClick,
}) => {
  // When the parent passes onEndReached we ALWAYS use Virtuoso (even for
  // small lists) so infinite scroll fires regardless of size. Otherwise
  // we fall back to the legacy threshold-based virtualisation.
  const useVirtual = !!onEndReached || jobs.length > VIRTUALIZATION_THRESHOLD;

  // Time-sort — shared preference with the Calendar/Timeline views.
  const [sortOrder, setSortOrder] = useState(() => readJobSortPref() || "desc");
  const toggleSort = () => {
    const next = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(next);
    writeJobSortPref(next);
  };
  const sortedJobs = useMemo(
    () => [...jobs].sort(compareBySchedule(sortOrder === "asc" ? 1 : -1)),
    [jobs, sortOrder]
  );

  const renderCard = (i, job) => (
    <div className="pb-3" key={job.job_id}>
      <JobCard
        job={job} index={i} onPreview={onPreview}
        handleEdit={handleEdit} confirmDelete={confirmDelete}
        handleSendReminder={handleSendReminder} handleSendToTech={handleSendToTech} sendingToTech={sendingToTech}
        onRenameJob={onRenameJob} sourceMap={sourceMap} onSourceClick={onSourceClick}
      />
    </div>
  );

  return (
    <div data-testid="job-list-view">
      {jobs.length > 0 && (
        <div className="flex items-center justify-end mb-2">
          <JobSortToggle sortOrder={sortOrder} onToggle={toggleSort} testId="list-sort-toggle" />
        </div>
      )}
      {jobs.length === 0 ? (
        <div className="text-center py-16 px-6">
          <div className="w-16 h-16 rounded-2xl bg-theme-surface border border-border/40 flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-7 h-7 text-muted-foreground/30" />
          </div>
          <p className="text-muted-foreground font-medium mb-1">No jobs found</p>
          <p className="text-sm text-muted-foreground/60 mb-5">Create your first job to get started</p>
          <Button className="btn-primary text-sm" onClick={openNewJobDialog} data-testid="create-first-job-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Job
          </Button>
        </div>
      ) : useVirtual ? (
        // Virtualized — only render the visible window of rows.
        // Auto-enables when jobs > 100 (large rosters / imported batches),
        // OR whenever the parent wires up infinite scroll via onEndReached.
        <Virtuoso
          useWindowScroll
          data={sortedJobs}
          itemContent={renderCard}
          increaseViewportBy={{ top: 400, bottom: 800 }}
          endReached={onEndReached}
          components={{
            Footer: () => (
              <div className="py-4 flex justify-center" data-testid="job-list-footer">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground animate-spin" />
                    Loading more jobs…
                  </div>
                ) : hasMore ? (
                  <div className="text-xs text-muted-foreground/60">Scroll for more</div>
                ) : null}
              </div>
            ),
          }}
          data-testid="job-list-virtuoso"
        />
      ) : (
        // Normal rendering for small lists — avoids the Virtuoso overhead.
        <div className="grid gap-3">
          {sortedJobs.map((job, i) => (
            <JobCard key={job.job_id} job={job} index={i} onPreview={onPreview}
              handleEdit={handleEdit} confirmDelete={confirmDelete}
              handleSendReminder={handleSendReminder} handleSendToTech={handleSendToTech} sendingToTech={sendingToTech}
              onRenameJob={onRenameJob} sourceMap={sourceMap} onSourceClick={onSourceClick} />
          ))}
        </div>
      )}
    </div>
  );
};

export default JobListView;
